import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { captureUtm } from "@/lib/utm";
import { Check, ArrowRight, ShieldCheck, CreditCard, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Plans() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { captureUtm(); }, []);
  const [loading, setLoading] = useState({});
  const [resuming, setResuming] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans));
  }, []);

  const showResume = !!user && user.subscription_status === "incomplete" && !!user.plan;
  const resumePlanName = user?.plan ? user.plan.charAt(0).toUpperCase() + user.plan.slice(1) : "";

  const startCheckout = async (planId, chosenInterval, key = "cta") => {
    setLoading((l) => ({ ...l, [key]: true }));
    try {
      const r = await api.post("/payments/checkout", {
        plan: planId,
        interval: chosenInterval,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setLoading((l) => ({ ...l, [key]: false }));
    }
  };

  const choose = (planId, chosenInterval) => {
    const key = `${planId}-${chosenInterval}`;
    if (!user) {
      nav(`/signup?plan=${planId}&interval=${chosenInterval}`);
      return;
    }
    startCheckout(planId, chosenInterval, key);
  };

  const resume = async () => {
    setResuming(true);
    try {
      await startCheckout(user.plan, user.billing_interval || "monthly", "resume");
    } finally {
      setResuming(false);
    }
  };

  // Flatten plans × intervals into one array of cards, ordered: monthly first, then annual.
  const cards = [];
  for (const interval of ["monthly", "annual"]) {
    for (const p of plans) {
      const pricing = p.pricing?.[interval];
      if (!pricing) continue;
      cards.push({ plan: p, interval, pricing });
    }
  }

  return (
    <div className="min-h-screen" style={{ background: "#f9f8f6" }}>
      <header className="border-b border-[#e5e2da]" style={{ background: "rgba(249,248,246,0.92)", backdropFilter: "blur(8px)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="plans-brand">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[#f9f8f6] font-serif-display text-xl" style={{ background: "#163325" }}>C</div>
            <span className="font-serif-display text-2xl">Coachly</span>
          </Link>
          {user ? (
            <Link to="/app" className="text-sm text-[#3c3c3c] hover:text-[#163325]" data-testid="plans-dashboard">Dashboard</Link>
          ) : (
            <Link to="/login" className="text-sm text-[#3c3c3c] hover:text-[#163325]" data-testid="plans-signin">Sign in</Link>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        {showResume && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="mb-10 rounded-2xl border border-[#163325] bg-[#163325] text-[#f9f8f6] p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            data-testid="resume-checkout-banner"
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "#d4a373", color: "#163325" }}>
                <Clock className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#d4a373]">Almost there</div>
                <div className="font-serif-display text-2xl mt-1 leading-tight">Finish setting up your {resumePlanName} trial.</div>
                <p className="text-sm opacity-80 mt-1 max-w-lg">Your account is created but Stripe never got your card details. Pick up where you left off — no data lost.</p>
              </div>
            </div>
            <Button
              onClick={resume}
              disabled={resuming || loading.resume}
              data-testid="resume-checkout-btn"
              className="rounded-full bg-[#d4a373] hover:bg-[#c99569] text-[#163325] h-11 px-6 shrink-0"
            >
              {resuming || loading.resume ? "Loading…" : "Resume checkout"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Choose your plan</div>
          <h1 className="font-serif-display text-5xl md:text-6xl mt-4 leading-tight">Pick a plan. <span className="deco-underline">7 days free.</span></h1>
          <p className="mt-4 text-[#5c5c5c] max-w-xl mx-auto">Card required on Starter trials. Cancel anytime before day 7 and you're never charged.</p>
        </motion.div>

        {/* Monthly row */}
        <div className="mt-14 flex items-baseline justify-between max-w-6xl mx-auto">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Pay monthly</div>
            <h2 className="font-serif-display text-3xl mt-1">Monthly billing</h2>
          </div>
          <div className="text-xs text-[#5c5c5c] hidden sm:block">Flexible · cancel anytime</div>
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-6 max-w-6xl mx-auto" data-testid="plans-row-monthly">
          {cards.filter((c) => c.interval === "monthly").map((c, i) => (
            <PlanCard key={`${c.plan.id}-monthly`} card={c} index={i} user={user} loading={loading} onChoose={choose} />
          ))}
        </div>

        {/* Annual row */}
        <div className="mt-16 flex items-baseline justify-between max-w-6xl mx-auto">
          <div className="flex items-baseline gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Pay annually</div>
              <h2 className="font-serif-display text-3xl mt-1">Annual billing</h2>
            </div>
            <Badge
              className="translate-y-[2px]"
              style={{ background: "#d4a373", color: "#163325", fontWeight: 500 }}
              data-testid="annual-save-badge"
            >
              Save 20%
            </Badge>
          </div>
          <div className="text-xs text-[#5c5c5c] hidden sm:block">2.4 months free · best value</div>
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-6 max-w-6xl mx-auto" data-testid="plans-row-annual">
          {cards.filter((c) => c.interval === "annual").map((c, i) => (
            <PlanCard key={`${c.plan.id}-annual`} card={c} index={i} user={user} loading={loading} onChoose={choose} />
          ))}
        </div>

        <div className="mt-14 flex flex-wrap justify-center gap-6 text-sm text-[#5c5c5c]" data-testid="plans-trust-row">
          <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#163325]" /> Secured by Stripe</div>
          <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#163325]" /> Cancel anytime</div>
          <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#163325]" /> No setup fees</div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ card, index, user, loading, onChoose }) {
  const { plan: p, interval, pricing } = card;
  const amount = pricing.amount;
  const unit = pricing.interval === "year" ? "/ yr" : "/ mo";
  const monthlyEq = pricing.monthly_equivalent;
  const savings = pricing.savings_pct;
  const monthlyAmount = p.pricing?.monthly?.amount || 0;
  const annualList = monthlyAmount * 12;
  const key = `${p.id}-${interval}`;
  const isAnnual = interval === "annual";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className={`rounded-2xl p-8 border relative ${p.highlight ? "border-[#163325] bg-[#163325] text-[#f9f8f6]" : "border-[#e5e2da] bg-white"}`}
      data-testid={`plan-card-${p.id}-${interval}`}
    >
      {p.highlight && (
        <Badge className="absolute -top-3 left-8" style={{ background: "#d4a373", color: "#163325", fontWeight: 500 }}>Most popular</Badge>
      )}
      {isAnnual && (
        <Badge
          className="absolute -top-3 right-8"
          style={{ background: p.highlight ? "#f9f8f6" : "#163325", color: p.highlight ? "#163325" : "#d4a373", fontWeight: 500 }}
          data-testid={`plan-savings-badge-${p.id}-${interval}`}
        >
          Save {savings}%
        </Badge>
      )}
      <div className={`text-xs uppercase tracking-[0.2em] ${p.highlight ? "text-[#d4a373]" : "text-[#8c9b86]"}`}>
        {p.name} <span className="opacity-70">· {isAnnual ? "Annual" : "Monthly"}</span>
      </div>
      <div className="mt-4 flex items-baseline gap-1" data-testid={`plan-price-${p.id}-${interval}`}>
        <span className="font-serif-display text-6xl">${amount.toLocaleString()}</span>
        <span className={p.highlight ? "opacity-70" : "text-[#5c5c5c]"}>{unit}</span>
      </div>
      <div className={`mt-2 text-sm ${p.highlight ? "opacity-80" : "text-[#5c5c5c]"}`}>
        {isAnnual ? (
          <>
            Works out to <b>${monthlyEq}/mo</b>
            <span className="line-through ml-2 opacity-60">${annualList.toLocaleString()}/yr</span>
            {p.trial_days > 0 && <> · free for {p.trial_days} days</>}
          </>
        ) : (
          <>
            {p.trial_days > 0 ? `Free for ${p.trial_days} days, then $${amount}/month.` : `Billed today. Full access immediately.`}
          </>
        )}
      </div>
      {isAnnual && (
        <div
          className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${p.highlight ? "bg-[#d4a373]/20 text-[#d4a373]" : "bg-[#163325]/8 text-[#163325]"}`}
          style={p.highlight ? {} : { background: "rgba(22,51,37,0.08)" }}
          data-testid={`plan-annual-savings-${p.id}`}
        >
          You save ${(annualList - amount).toLocaleString()}/yr
        </div>
      )}
      <ul className={`mt-6 space-y-2 text-sm ${p.highlight ? "opacity-90" : "text-[#3c3c3c]"}`}>
        {p.features.map((f, j) => (
          <li key={j} className="flex items-start gap-2"><Check className={`w-4 h-4 mt-0.5 ${p.highlight ? "text-[#d4a373]" : "text-[#163325]"}`} /> {f}</li>
        ))}
      </ul>
      <Button
        onClick={() => onChoose(p.id, interval)}
        disabled={loading[key]}
        data-testid={`plan-cta-${p.id}-${interval}`}
        className={`w-full rounded-full h-11 mt-8 ${p.highlight ? "bg-[#d4a373] hover:bg-[#c99569] text-[#163325]" : "bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6]"}`}
      >
        {loading[key] ? "Loading…" : (user ? "Continue" : (p.trial_days > 0 ? `Start ${p.trial_days}-day trial` : "Subscribe now"))}
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
      <div className={`mt-4 flex items-center gap-2 text-xs ${p.highlight ? "opacity-70" : "text-[#5c5c5c]"}`}>
        <CreditCard className="w-3.5 h-3.5" /> {p.trial_days > 0 ? "Card required · billed after trial" : "Card required · billed today"}
      </div>
    </motion.div>
  );
}
